import {useEffect, useState} from "react";
import {getBookings, getBookingById} from "../api/api";
import {getReferrals} from "../api/api";
import {getUserFeedback} from "../api/api";
import {useAuth} from "../context/AuthContext";
import {formatCurrency} from "../lib/utils";
import {Tabs, TabsContent, TabsList, TabsTrigger} from "../components/ui/tabs";
import {Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle} from "../components/ui/card";
import {Avatar, AvatarFallback, AvatarImage} from "../components/ui/avatar";
import {Button} from "../components/ui/button";
import {Input} from "../components/ui/input";
import {Label} from "../components/ui/label";
import {Skeleton} from "../components/ui/skeleton";
import {Badge} from "../components/ui/badge";
import {Helmet} from "react-helmet";
import {useToast} from "../hooks/use-toast";
import { isBefore, isAfter, parseISO } from "date-fns"; // Install date-fns if not already
import {
    Calendar,
    Clock,
    Users,
    Star,
    Gift,
    Settings,
    History,
    Bell,
    X as XIcon,
    Eye,
} from "lucide-react";
import {Booking} from "../types";
import {
    updateProfile,
    changePassword,
    getNotificationSettings,
    updateNotificationSettings, NotificationSettings,
} from "../api/api";


const Profile = () => {
    const {user, isAuthenticated, isLoading: isAuthLoading} = useAuth();
    const {toast} = useToast();

    const [currentTab, setCurrentTab] = useState("");
    const [selectedBooking, setSelectedBooking] = useState<Booking | null>(null);
    const [isDetailOpen, setIsDetailOpen] = useState(false);
    const [firstName, setFirstName] = useState(user?.first_name || "");
    const [lastName, setLastName] = useState(user?.last_name || "");
    const [email, setEmail] = useState(user?.email || "");
    const [isSavingProfile, setIsSavingProfile] = useState(false);

    const [currentPassword, setCurrentPassword] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [isChangingPassword, setIsChangingPassword] = useState(false);

    const [notifSettings, setNotifSettings] = useState<NotificationSettings | null>(null);
    const [isLoadingNotifications, setIsLoadingNotifications] = useState(true);
    const [isUpdatingNotifications, setIsUpdatingNotifications] = useState(false);

    const [bookings, setBookings] = useState<Booking[] | null>(null);
    const [isLoadingBookings, setIsLoadingBookings] = useState(true);

    const [referrals, setReferrals] = useState<any[] | null>(null);
    const [isLoadingReferrals, setIsLoadingReferrals] = useState(true);

    const [feedbackItems, setFeedbackItems] = useState<any[] | null>(null);
    const [isLoadingFeedback, setIsLoadingFeedback] = useState(true);

    useEffect(() => {
        if (!isAuthenticated) return;



        getNotificationSettings()
            .then(setNotifSettings)
            .catch(() => toast({title: "Error", description: "Failed to load notifications."}))
            .finally(() => setIsLoadingNotifications(false));

        if (!user?.is_restaurant_owner) {
            setCurrentTab("bookings");
            getBookings(user?.id || 0)
                .then(setBookings)
                .catch(() => toast({title: "Error", description: "Failed to load bookings."}))
                .finally(() => setIsLoadingBookings(false));

            getUserFeedback(user?.id || 0)
                .then(setFeedbackItems)
                .catch(() => toast({title: "Error", description: "Failed to load feedback."}))
                .finally(() => setIsLoadingFeedback(false));
        } else {
            setCurrentTab("referrals");
        }

        getReferrals(user?.id || 0)
            .then(setReferrals)
            .catch(() => toast({title: "Error", description: "Failed to load referrals."}))
            .finally(() => setIsLoadingReferrals(false));
    }, [isAuthenticated]);

    const handleSaveProfile = async () => {
        setIsSavingProfile(true);
        try {
            await updateProfile(user!.id, {first_name: firstName, last_name: lastName, email});
            toast({title: "Profile updated", description: "Your profile has been successfully updated."});
        } catch {
            toast({title: "Error", description: "Failed to update profile."});
        } finally {
            setIsSavingProfile(false);
        }
    };

    const handleSavePassword = async () => {
        if (newPassword !== confirmPassword) {
            toast({title: "Error", description: "Passwords do not match."});
            return;
        }
        setIsChangingPassword(true);
        try {
            await changePassword(currentPassword, newPassword);
            toast({title: "Password updated", description: "Your password has been successfully changed."});
            setCurrentPassword("");
            setNewPassword("");
            setConfirmPassword("");
        } catch (error: any) {
            toast({title: "Error", description: error.message || "Failed to change password."});
        } finally {
            setIsChangingPassword(false);
        }
    };

    const handleSaveNotifications = async () => {
        if (!notifSettings) return;
        setIsUpdatingNotifications(true);
        try {
            await updateNotificationSettings(notifSettings);
            toast({title: "Notification preferences saved", description: "Your notification preferences have been updated."});
        } catch {
            toast({title: "Error", description: "Failed to save notification preferences."});
        } finally {
            setIsUpdatingNotifications(false);
        }
    };

    const openDetails = async (id: number) => {
        try {
            const data = await getBookingById(id);
            setSelectedBooking(data);
            setIsDetailOpen(true);
        } catch (e) {
            console.error(e);
            toast({title: "Error", description: "Failed to load booking details."});
        }
    };

    const closeDetails = () => {
        setIsDetailOpen(false);
        setSelectedBooking(null);
    };

    if (isAuthLoading) {
        return (
            <div className="container mx-auto max-w-4xl px-4 py-16 sm:px-6 lg:px-8">
                <div className="flex flex-col items-center space-y-4">
                    <Skeleton className="h-24 w-24 rounded-full"/>
                    <Skeleton className="h-8 w-48"/>
                    <Skeleton className="h-6 w-64"/>
                </div>
            </div>
        );
    }

    if (!isAuthenticated || !user) {
        return (
            <div className="container mx-auto max-w-4xl px-4 py-16 sm:px-6 lg:px-8">
                <div className="rounded-lg bg-white p-8 text-center shadow dark:bg-gray-800">
                    <h1 className="mb-4 text-2xl font-bold text-gray-800 dark:text-white">
                        Login Required
                    </h1>
                    <p className="mb-6 text-gray-600 dark:text-gray-300">
                        Please login to view your profile.
                    </p>
                    <Button asChild className="bg-primary text-white hover:bg-red-700">
                        <a href="/public">Return to Home</a>
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <>
            <Helmet>
                <title>My Profile | SurpriseTable</title>
                <meta
                    name="description"
                    content="Manage your profile, view bookings, track your referrals, and update account settings."
                />
            </Helmet>

            <div className="container mx-auto max-w-4xl px-4 py-16 sm:px-6 lg:px-8">
                <div className="mb-12 flex flex-col items-center md:flex-row md:items-start md:justify-between">
                    <div className="flex flex-col items-center md:flex-row md:space-x-4">
                        <Avatar className="h-24 w-24">
                            <AvatarImage src={user?.avatar ? user?.avatar : undefined} alt={user.first_name}/>
                            <AvatarFallback className="bg-primary text-xl text-white">
                                {user.first_name?.[0]}{user.last_name?.[0]}
                            </AvatarFallback>
                        </Avatar>
                        <div className="mt-4 text-center md:mt-0 md:text-left">
                            <h1 className="text-2xl font-bold text-gray-800 dark:text-white">
                                {user.first_name} {user.last_name}
                            </h1>
                            <p className="text-gray-600 dark:text-gray-300">{user.email}</p>
                            <div className="mt-2">
                                <Badge className="bg-primary hover:bg-primary-dark">
                                    {user.is_restaurant_owner ? "Restaurant Owner" : "Member"}
                                </Badge>
                            </div>
                        </div>
                    </div>
                    <div className="mt-6 md:mt-0">
                        <Button variant="outline" asChild>
                            <a href="/referral">Refer & Earn</a>
                        </Button>
                    </div>
                </div>

                <Tabs
                    defaultValue={`${user.is_restaurant_owner ? "referrals" : "bookings"}`}
                    value={currentTab}
                    onValueChange={setCurrentTab}
                    className="space-y-6"
                >
                    <TabsList className="w-full justify-start overflow-x-auto">
                        <TabsTrigger value="bookings" className={`flex items-center gap-2 ${user.is_restaurant_owner && "hidden"}`}>
                            <History className="h-4 w-4"/>
                            <span>Bookings</span>
                        </TabsTrigger>
                        <TabsTrigger value="referrals" className="flex items-center gap-2">
                            <Gift className="h-4 w-4"/>
                            <span>Referrals</span>
                        </TabsTrigger>
                        <TabsTrigger value="feedback" className={`flex items-center gap-2 ${user.is_restaurant_owner && "hidden"}`}>
                            <Star className="h-4 w-4"/>
                            <span>My Feedback</span>
                        </TabsTrigger>
                        <TabsTrigger value="notifications" className="flex items-center gap-2">
                            <Bell className="h-4 w-4"/>
                            <span>Notifications</span>
                        </TabsTrigger>
                        <TabsTrigger value="settings" className="flex items-center gap-2">
                            <Settings className="h-4 w-4"/>
                            <span>Settings</span>
                        </TabsTrigger>
                    </TabsList>

                    <TabsContent value="bookings" className="space-y-8">
                        <h2 className="text-xl font-semibold text-gray-800 dark:text-white">
                            Your Bookings
                        </h2>
                        {isLoadingBookings ? (
                            <div className="space-y-4">
                                {[1, 2, 3].map((i) => (
                                    <Card key={i}>
                                        <CardHeader>
                                            <Skeleton className="h-6 w-48" />
                                            <Skeleton className="h-4 w-32" />
                                        </CardHeader>
                                        <CardContent>
                                            <div className="space-y-2">
                                                <Skeleton className="h-4 w-full" />
                                                <Skeleton className="h-4 w-3/4" />
                                            </div>
                                        </CardContent>
                                    </Card>
                                ))}
                            </div>
                        ) : !bookings || bookings.length === 0 ? (
                            <Card>
                                <CardContent className="pt-6 text-center">
                                    <p className="text-gray-600 dark:text-gray-300">
                                        You don't have any bookings yet.
                                    </p>
                                    <Button asChild className="mt-4 bg-primary text-white hover:bg-red-700">
                                        <a href="/#booking-section">Book a Surprise</a>
                                    </Button>
                                </CardContent>
                            </Card>
                        ) : (
                            (() => {
                                const now = new Date();

                                // Split upcoming and past, sorted accordingly
                                const upcoming = bookings
                                    .filter(
                                        (b) =>
                                            isAfter(parseISO(`${b.date}T${b.time}`), now) ||
                                            b.status === "confirmed"
                                    )
                                    .sort(
                                        (a, b) =>
                                            parseISO(`${a.date}T${a.time}`).getTime() - parseISO(`${b.date}T${b.time}`).getTime()
                                    );

                                const past = bookings
                                    .filter(
                                        (b) =>
                                            isBefore(parseISO(`${b.date}T${b.time}`), now) &&
                                            b.status !== "confirmed"
                                    )
                                    .sort(
                                        (a, b) =>
                                            parseISO(`${b.date}T${b.time}`).getTime() - parseISO(`${a.date}T${a.time}`).getTime()
                                    );

                                return (
                                    <div className="space-y-10">
                                        {/* UPCOMING BOOKINGS */}
                                        <div>
                                            <div className="flex items-center gap-2 mb-2">
                                                <Calendar className="h-5 w-5 text-primary" />
                                                <h3 className="text-lg font-bold">Upcoming</h3>
                                            </div>
                                            {upcoming.length === 0 ? (
                                                <Card>
                                                    <CardContent className="py-6 text-center text-sm text-gray-500 dark:text-gray-400">
                                                        No upcoming bookings.
                                                    </CardContent>
                                                </Card>
                                            ) : (
                                                <div className="grid md:grid-cols-2 gap-4">
                                                    {upcoming.map((booking) => (
                                                        <Card
                                                            key={booking.id}
                                                            className="hover:shadow-lg transition-shadow"
                                                        >
                                                            <CardHeader className="flex flex-row items-center justify-between gap-2">
                                                                <div>
                                                                    <CardTitle>
                                                                        {booking.restaurant?.name || "Restaurant"}
                                                                    </CardTitle>
                                                                    <CardDescription className="flex items-center gap-2 mt-1">
                                                                        <Badge
                                                                            className={
                                                                                booking.status === "confirmed"
                                                                                    ? "bg-green-500"
                                                                                    : "bg-blue-500"
                                                                            }
                                                                        >
                                                                            {booking.status[0].toUpperCase() +
                                                                                booking.status.slice(1)}
                                                                        </Badge>
                                                                    </CardDescription>
                                                                </div>
                                                                <Avatar className="h-12 w-12">
                                                                    <AvatarImage
                                                                        src={

                                                                            "/default-restaurant.png"
                                                                        }
                                                                        alt={booking.restaurant?.name}
                                                                    />
                                                                    <AvatarFallback>
                                                                        {(booking.restaurant?.name || "?")[0]}
                                                                    </AvatarFallback>
                                                                </Avatar>
                                                            </CardHeader>
                                                            <CardContent>
                                                                <div className="flex flex-col gap-1 text-sm">
                                                                    <div className="flex items-center gap-2">
                                                                        <Calendar className="h-4 w-4" />
                                                                        <span>{booking.date}</span>
                                                                    </div>
                                                                    <div className="flex items-center gap-2">
                                                                        <Clock className="h-4 w-4" />
                                                                        <span>{booking.time}</span>
                                                                    </div>
                                                                    <div className="flex items-center gap-2">
                                                                        <Users className="h-4 w-4" />
                                                                        <span>
                            {booking.guests}{" "}
                                                                            {booking.guests === 1 ? "person" : "people"}
                          </span>
                                                                    </div>
                                                                    <div className="flex items-center gap-2">
                                                                        <span className="font-medium">Total:</span>
                                                                        <span>{formatCurrency(booking.total_price)}</span>
                                                                    </div>
                                                                    {booking.code_to_verify && (
                                                                        <div className="flex items-center gap-2">
                                                                            <span className="font-medium">Code:</span>
                                                                            <span>{booking.code_to_verify}</span>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </CardContent>
                                                            <CardFooter className="flex gap-2 justify-end">
                                                                <Button
                                                                    size="sm"
                                                                    className="bg-primary hover:bg-red-700"
                                                                    onClick={() => openDetails(booking.id)}
                                                                >
                                                                    View Details
                                                                </Button>
                                                                {booking.status === "confirmed" && (
                                                                    <Button variant="destructive" size="sm">
                                                                        Cancel
                                                                    </Button>
                                                                )}
                                                            </CardFooter>
                                                        </Card>
                                                    ))}
                                                </div>
                                            )}
                                        </div>

                                        {/* PAST BOOKINGS */}
                                        <div>
                                            <div className="flex items-center gap-2 mb-2">
                                                <History className="h-5 w-5 text-blue-500" />
                                                <h3 className="text-lg font-bold">Past Bookings</h3>
                                            </div>
                                            {past.length === 0 ? (
                                                <Card>
                                                    <CardContent className="py-6 text-center text-sm text-gray-500 dark:text-gray-400">
                                                        No past bookings yet.
                                                    </CardContent>
                                                </Card>
                                            ) : (
                                                <div className="grid md:grid-cols-2 gap-4">
                                                    {past.map((booking) => (
                                                        <Card
                                                            key={booking.id}
                                                            className="hover:shadow-lg opacity-80 transition-all"
                                                        >
                                                            <CardHeader className="flex flex-row items-center justify-between gap-2">
                                                                <div>
                                                                    <CardTitle>
                                                                        {booking.restaurant?.name || "Restaurant"}
                                                                    </CardTitle>
                                                                    <CardDescription className="flex items-center gap-2 mt-1">
                                                                        <Badge
                                                                            className={
                                                                                booking.status === "completed"
                                                                                    ? "bg-blue-500"
                                                                                    : "bg-red-500"
                                                                            }
                                                                        >
                                                                            {booking.status[0].toUpperCase() +
                                                                                booking.status.slice(1)}
                                                                        </Badge>
                                                                    </CardDescription>
                                                                </div>
                                                                <Avatar className="h-12 w-12">
                                                                    <AvatarImage
                                                                        src={

                                                                            "/default-restaurant.png"
                                                                        }
                                                                        alt={booking.restaurant?.name}
                                                                    />
                                                                    <AvatarFallback>
                                                                        {(booking.restaurant?.name || "?")[0]}
                                                                    </AvatarFallback>
                                                                </Avatar>
                                                            </CardHeader>
                                                            <CardContent>
                                                                <div className="flex flex-col gap-1 text-sm">
                                                                    <div className="flex items-center gap-2">
                                                                        <Calendar className="h-4 w-4" />
                                                                        <span>{booking.date}</span>
                                                                    </div>
                                                                    <div className="flex items-center gap-2">
                                                                        <Clock className="h-4 w-4" />
                                                                        <span>{booking.time}</span>
                                                                    </div>
                                                                    <div className="flex items-center gap-2">
                                                                        <Users className="h-4 w-4" />
                                                                        <span>
                            {booking.guests}{" "}
                                                                            {booking.guests === 1 ? "person" : "people"}
                          </span>
                                                                    </div>
                                                                    <div className="flex items-center gap-2">
                                                                        <span className="font-medium">Total:</span>
                                                                        <span>{formatCurrency(booking.total_price)}</span>
                                                                    </div>
                                                                </div>
                                                            </CardContent>
                                                            <CardFooter className="flex gap-2 justify-end">
                                                                {booking.status === "completed" && (
                                                                    <Button
                                                                        asChild
                                                                        variant="outline"
                                                                        size="sm"
                                                                        className="border-primary text-primary hover:bg-primary/10 hover:text-primary"
                                                                    >
                                                                        <a href="/feedback">Leave Feedback</a>
                                                                    </Button>
                                                                )}
                                                                <Button
                                                                    size="sm"
                                                                    variant="ghost"
                                                                    onClick={() => openDetails(booking.id)}
                                                                >
                                                                    View Details
                                                                </Button>
                                                            </CardFooter>
                                                        </Card>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                );
                            })()
                        )}
                    </TabsContent>

                    <TabsContent value="referrals" className="space-y-6">
                        <h2 className="text-xl font-semibold text-gray-800 dark:text-white">
                            Your Referrals
                        </h2>
                        <Card className="mb-6">
                            <CardHeader>
                                <CardTitle>Your Referral Code</CardTitle>
                                <CardDescription>
                                    Share this code with friends to earn rewards
                                </CardDescription>
                            </CardHeader>
                            <CardContent>
                                <div className="flex items-center gap-2">
                                    <div className="flex-1 rounded-md bg-gray-100 px-4 py-2 font-mono dark:bg-gray-800">
                                        {user.referral_code || "JOHN2023"}
                                    </div>
                                    <Button
                                        onClick={() => {
                                            navigator.clipboard.writeText(user?.referral_code || "JOHN2023");
                                            toast({
                                                title: "Copied!",
                                                description: "Referral code copied to clipboard",
                                            });
                                        }}
                                        className="bg-primary hover:bg-red-700"
                                    >
                                        Copy
                                    </Button>
                                </div>
                            </CardContent>
                            <CardFooter>
                                <Button
                                    variant="outline"
                                    onClick={() => {
                                        const url = `${window.location.origin}/register?ref=${
                                            user?.referral_code || "JOHN2023"
                                        }`;
                                        window.open(
                                            `mailto:?subject=Join%20SurpriseTable&body=Use%20my%20referral%20code%20${
                                                user.referral_code || "JOHN2023"
                                            }%20to%20get%20€5%20off%20your%20first%20booking!%20${encodeURIComponent(
                                                url
                                            )}`,
                                            "_blank"
                                        );
                                    }}
                                    className="w-full"
                                >
                                    Share via Email
                                </Button>
                            </CardFooter>
                        </Card>

                        {isLoadingReferrals ? (
                            <div className="space-y-4">
                                {[1, 2].map((i) => (
                                    <Card key={i}>
                                        <CardHeader>
                                            <Skeleton className="h-6 w-48"/>
                                        </CardHeader>
                                        <CardContent>
                                            <Skeleton className="h-4 w-full"/>
                                            <Skeleton className="mt-2 h-4 w-3/4"/>
                                        </CardContent>
                                    </Card>
                                ))}
                            </div>
                        ) : !referrals || referrals.length === 0 ? (
                            <Card>
                                <CardContent className="pt-6 text-center">
                                    <p className="text-gray-600 dark:text-gray-300">
                                        You haven't referred anyone yet.
                                    </p>
                                    <Button
                                        asChild
                                        className="mt-4 bg-primary text-white hover:bg-red-700"
                                    >
                                        <a href="/referral">Start Referring</a>
                                    </Button>
                                </CardContent>
                            </Card>
                        ) : (
                            <div className="space-y-4">
                                {referrals.map((referral) => (
                                    <Card key={referral.id}>
                                        <CardHeader>
                                            <CardTitle>
                                                {referral.referred?.firstName || "Friend"}{" "}
                                                {referral.referred?.lastName || ""}
                                            </CardTitle>
                                            <CardDescription>
                                                <Badge
                                                    className={
                                                        referral.status === "completed"
                                                            ? "bg-green-500"
                                                            : "bg-yellow-500"
                                                    }
                                                >
                                                    {referral.status.charAt(0).toUpperCase() + referral.status.slice(1)}
                                                </Badge>
                                            </CardDescription>
                                        </CardHeader>
                                        <CardContent>
                                            <p className="text-sm text-gray-600 dark:text-gray-300">
                                                {referral.status === "completed"
                                                    ? `You earned ${formatCurrency(referral.rewardAmount)} when they completed their first booking.`
                                                    : "Pending their first booking."}
                                            </p>
                                            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                                                Referred on{" "}
                                                {referral.createdAt
                                                    ? new Date(referral.createdAt).toLocaleDateString()
                                                    : "Unknown date"}
                                            </p>
                                        </CardContent>
                                    </Card>
                                ))}
                            </div>
                        )}
                    </TabsContent>

                    <TabsContent value="feedback" className="space-y-6">
                        <h2 className="text-xl font-semibold text-gray-800 dark:text-white">
                            Your Feedback
                        </h2>
                        {isLoadingFeedback ? (
                            <div className="space-y-4">
                                {[1, 2].map((i) => (
                                    <Card key={i}>
                                        <CardHeader>
                                            <Skeleton className="h-6 w-48"/>
                                        </CardHeader>
                                        <CardContent>
                                            <Skeleton className="h-4 w-full"/>
                                            <Skeleton className="mt-2 h-4 w-3/4"/>
                                        </CardContent>
                                    </Card>
                                ))}
                            </div>
                        ) : !feedbackItems || feedbackItems.length === 0 ? (
                            <Card>
                                <CardContent className="pt-6 text-center">
                                    <p className="text-gray-600 dark:text-gray-300">
                                        You haven't left any feedback yet.
                                    </p>
                                    <Button
                                        asChild
                                        className="mt-4 bg-primary text-white hover:bg-red-700"
                                    >
                                        <a href="/feedback">Leave Feedback</a>
                                    </Button>
                                </CardContent>
                            </Card>
                        ) : (
                            <div className="space-y-4">
                                {feedbackItems.map((feedback) => (
                                    <Card key={feedback.id}>
                                        <CardHeader>
                                            <CardTitle>
                                                {feedback.booking?.restaurant?.name || "Restaurant"}
                                            </CardTitle>
                                            <CardDescription>
                                                <div className="flex">
                                                    {Array(5)
                                                        .fill(0)
                                                        .map((_, i) => (
                                                            <Star
                                                                key={i}
                                                                className={`h-4 w-4 ${
                                                                    i < feedback.rating
                                                                        ? "fill-yellow-400 text-yellow-400"
                                                                        : "fill-gray-200 text-gray-200 dark:fill-gray-700 dark:text-gray-700"
                                                                }`}
                                                            />
                                                        ))}
                                                </div>
                                            </CardDescription>
                                        </CardHeader>
                                        <CardContent>
                                            <p className="text-sm text-gray-600 dark:text-gray-300">
                                                {feedback.comment || "No comment provided."}
                                            </p>
                                            <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                                                Submitted on{" "}
                                                {feedback.created_at
                                                    ? new Date(feedback.created_at).toLocaleDateString()
                                                    : "Unknown date"}
                                            </p>
                                        </CardContent>
                                        <CardFooter className="flex justify-end space-x-2">
                                            <Button variant="destructive" size="sm">
                                                Delete
                                            </Button>
                                        </CardFooter>
                                    </Card>
                                ))}
                            </div>
                        )}
                    </TabsContent>

                    <TabsContent value="notifications" className="space-y-6">
                        <h2 className="text-xl font-semibold text-gray-800 dark:text-white">
                            Notification Preferences
                        </h2>
                        <Card>
                            <CardContent className="pt-6">
                                <div className="space-y-4">
                                    { /* Loading / error states */ }
                                    {isLoadingNotifications ? (
                                        <div className="text-center py-4">Loading…</div>
                                    ) : (
                                        <>
                                            <div className="flex items-center justify-between">
                                                <Label
                                                    htmlFor="emailNotifications"
                                                    className="flex-1 text-gray-800 dark:text-white"
                                                >
                                                    Email Notifications
                                                </Label>
                                                <input
                                                    type="checkbox"
                                                    id="emailNotifications"
                                                    checked={notifSettings?.email_notifications ?? false}
                                                    onChange={e =>
                                                        setNotifSettings(prev =>
                                                            prev
                                                                ? { ...prev, email_notifications: e.target.checked }
                                                                : prev
                                                        )
                                                    }
                                                    className="h-5 w-5 rounded border-gray-300 text-primary focus:ring-primary"
                                                />
                                            </div>

                                            <div className="flex items-center justify-between">
                                                <Label
                                                    htmlFor="bookingReminders"
                                                    className="flex-1 text-gray-800 dark:text-white"
                                                >
                                                    Booking Reminders
                                                </Label>
                                                                                                <input
                                                    type="checkbox"
                                                    id="emailNotifications"
                                                    checked={notifSettings?.booking_reminders ?? false}
                                                    onChange={e =>
                                                        setNotifSettings(prev =>
                                                            prev
                                                                ? { ...prev, booking_reminders: e.target.checked }
                                                                : prev
                                                        )
                                                    }
                                                    className="h-5 w-5 rounded border-gray-300 text-primary focus:ring-primary"
                                                />
                                            </div>

                                            <div className="flex items-center justify-between">
                                                <Label
                                                    htmlFor="specialOffers"
                                                    className="flex-1 text-gray-800 dark:text-white"
                                                >
                                                    Special Offers
                                                </Label>

                                                <input
                                                    type="checkbox"
                                                    id="emailNotifications"
                                                    checked={notifSettings?.special_offers ?? false}
                                                    onChange={e =>
                                                        setNotifSettings(prev =>
                                                            prev
                                                                ? { ...prev, special_offers: e.target.checked }
                                                                : prev
                                                        )
                                                    }
                                                    className="h-5 w-5 rounded border-gray-300 text-primary focus:ring-primary"
                                                />

                                            </div>

                                            <div className="flex items-center justify-between">
                                                <Label
                                                    htmlFor="referralUpdates"
                                                    className="flex-1 text-gray-800 dark:text-white"
                                                >
                                                    Referral Updates
                                                </Label>
                                                <input
                                                    type="checkbox"
                                                    id="emailNotifications"
                                                    checked={notifSettings?.referral_updates ?? false}
                                                    onChange={e =>
                                                        setNotifSettings(prev =>
                                                            prev
                                                                ? { ...prev, referral_updates: e.target.checked }
                                                                : prev
                                                        )
                                                    }
                                                    className="h-5 w-5 rounded border-gray-300 text-primary focus:ring-primary"
                                                />
                                            </div>

                                            <div className="flex items-center justify-between">
                                                <Label
                                                    htmlFor="newsletter"
                                                    className="flex-1 text-gray-800 dark:text-white"
                                                >
                                                    Newsletter
                                                </Label>
                                                <input
                                                    type="checkbox"
                                                    id="emailNotifications"
                                                    checked={notifSettings?.newsletter ?? false}
                                                    onChange={e =>
                                                        setNotifSettings(prev =>
                                                            prev
                                                                ? { ...prev, newsletter: e.target.checked }
                                                                : prev
                                                        )
                                                    }
                                                    className="h-5 w-5 rounded border-gray-300 text-primary focus:ring-primary"
                                                />

                                            </div>
                                        </>
                                    )}
                                </div>
                            </CardContent>
                            <CardFooter>
                                <Button
                                    onClick={async () => {
                                        if (!notifSettings) return;
                                        try {
                                            await updateNotificationSettings(notifSettings);
                                            toast({
                                                title: "Notification preferences saved",
                                                description: "Your notification preferences have been updated.",
                                            });
                                        } catch {
                                            toast({
                                                title: "Error",
                                                description: "Failed to save notification preferences.",
                                            });
                                        }
                                    }}
                                    disabled={isUpdatingNotifications}
                                    className="w-full bg-primary text-white hover:bg-red-700"
                                >
                                    {isUpdatingNotifications ? "Saving…" : "Save Preferences"}
                                </Button>
                            </CardFooter>
                        </Card>
                    </TabsContent>


                    <TabsContent value="settings" className="space-y-6">
                        <h2 className="text-xl font-semibold text-gray-800 dark:text-white">
                            Profile Settings
                        </h2>
                        <Card>
                            <CardHeader>
                                <CardTitle>Personal Information</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="grid gap-4 sm:grid-cols-2">
                                    <div className="space-y-1">
                                        <Label
                                            htmlFor="firstName"
                                            className="text-gray-700 dark:text-gray-300"
                                        >
                                            First Name
                                        </Label>
                                        <Input
                                            id="firstName"
                                            value={firstName}
                                            onChange={e => setFirstName(e.target.value)}
                                            className="border-gray-300 dark:border-gray-700 dark:bg-gray-800"
                                        />

                                    </div>
                                    <div className="space-y-1">
                                        <Label
                                            htmlFor="lastName"
                                            className="text-gray-700 dark:text-gray-300"
                                        >
                                            Last Name
                                        </Label>
                                        <Input
                                            id="lastName"
                                            value={lastName}
                                            onChange={e => setLastName(e.target.value)}
                                            className="border-gray-300 dark:border-gray-700 dark:bg-gray-800"
                                        />


                                    </div>
                                    <div className="space-y-1 sm:col-span-2">
                                        <Label
                                            htmlFor="email"
                                            className="text-gray-700 dark:text-gray-300"
                                        >
                                            Email
                                        </Label>
                                        <Input
                                            id="email"
                                            type="email"
                                            value={email}
                                            onChange={e => setEmail(e.target.value)}
                                            className="border-gray-300 dark:border-gray-700 dark:bg-gray-800"
                                        />

                                    </div>
                                </div>
                            </CardContent>
                            <CardFooter>
                                {/*<Button*/}
                                {/*    onClick={handleSaveProfile}*/}
                                {/*    className="w-full bg-primary text-white hover:bg-red-700"*/}
                                {/*>*/}
                                {/*    Save Changes*/}
                                {/*</Button>*/}
                                <Button                                     className="w-full bg-primary text-white hover:bg-red-700"
                                                                            onClick={handleSaveProfile} disabled={isSavingProfile}>
                                     {isSavingProfile ? "Saving…" : "Save Changes"}
                                    </Button>
                            </CardFooter>
                        </Card>

                        <Card>
                            <CardHeader>
                                <CardTitle>Change Password</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="space-y-4">
                                    <div className="space-y-1">
                                        <Label
                                            htmlFor="currentPassword"
                                            className="text-gray-700 dark:text-gray-300"
                                        >
                                            Current Password
                                        </Label>
                                        <Input
                                            id="currentPassword"
                                            type="password"
                                            value={currentPassword}
                                            onChange={e => setCurrentPassword(e.target.value)}
                                            className="border-gray-300 dark:border-gray-700 dark:bg-gray-800"
                                        />
                                    </div>
                                    <div className="space-y-1">
                                        <Label
                                            htmlFor="newPassword"
                                            className="text-gray-700 dark:text-gray-300"
                                        >
                                            New Password
                                        </Label>
                                        <Input
                                            id="newPassword"
                                            type="password"
                                            value={newPassword}
                                            onChange={e => setNewPassword(e.target.value)}
                                            className="border-gray-300 dark:border-gray-700 dark:bg-gray-800"
                                        />
                                    </div>
                                    <div className="space-y-1">
                                        <Label
                                            htmlFor="confirmPassword"
                                            className="text-gray-700 dark:text-gray-300"
                                        >
                                            Confirm New Password
                                        </Label>
                                        <Input
                                            id="confirmPassword"
                                            type="password"
                                            value={confirmPassword}
                                            onChange={e => setConfirmPassword(e.target.value)}
                                            className="border-gray-300 dark:border-gray-700 dark:bg-gray-800"
                                        />
                                    </div>
                                </div>
                            </CardContent>
                            <CardFooter>
                                <Button
                                    onClick={handleSavePassword}
                                    disabled={isChangingPassword}
                                    className="w-full bg-primary text-white hover:bg-red-700"
                                >
                                    {isChangingPassword ? "Updating…" : "Update Password"}
                                </Button>
                            </CardFooter>
                        </Card>

                        <Card>
                            <CardHeader>
                                <CardTitle>Delete Account</CardTitle>
                                <CardDescription>
                                    This action cannot be undone. This will permanently delete your
                                    account and all data associated with it.
                                </CardDescription>
                            </CardHeader>
                            <CardFooter>
                                <Button variant="destructive" className="w-full">
                                    Delete Account
                                </Button>
                            </CardFooter>
                        </Card>
                    </TabsContent>
                </Tabs>
            </div>
            {isDetailOpen && selectedBooking && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 p-4 backdrop-blur-sm">
                    <div className="relative bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-lg p-8">
                        {/* Close button (floating) */}
                        <button
                            onClick={closeDetails}
                            className="absolute top-4 right-4 text-gray-400 hover:text-red-500 transition"
                            aria-label="Close"
                        >
                            <XIcon className="h-6 w-6" />
                        </button>
                        {/* Modal Title */}
                        <div className="mb-5 flex items-center gap-3">
                            <Calendar className="h-7 w-7 text-primary" />
                            <h2 className="text-2xl font-bold text-gray-800 dark:text-white flex-1">
                                Booking Details
                            </h2>
                            <Badge
                                className={
                                    selectedBooking.status === "confirmed"
                                        ? "bg-green-500"
                                        : selectedBooking.status === "completed"
                                            ? "bg-blue-500"
                                            : "bg-red-500"
                                }
                            >
                                {selectedBooking.status[0].toUpperCase() +
                                    selectedBooking.status.slice(1)}
                            </Badge>
                        </div>

                        {/* 6-Digit Confirmation Code */}
                        <div className="mb-6 rounded-xl bg-primary/10 dark:bg-primary/20 border border-primary/20 flex flex-col items-center p-5">
        <span className="uppercase text-xs font-semibold text-primary tracking-widest mb-2">
          Confirmation Code for Restaurant
        </span>
                            <div className="flex items-center gap-2">
                                {selectedBooking.code_to_verify ? (
                                    <>
              <span className="text-3xl font-mono font-bold tracking-widest text-primary bg-white dark:bg-gray-900 rounded-lg px-4 py-2 shadow-inner border border-primary/40">
                {selectedBooking.code_to_verify}
              </span>
                                        <button
                                            onClick={() => {
                                                navigator.clipboard.writeText(String(selectedBooking.code_to_verify ?? ""));
                                                // Optionally add a toast/notification here
                                            }}
                                            title="Copy Code"
                                            className="ml-1 p-1 text-gray-400 hover:text-primary transition"
                                        >
                                            <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                                <rect x="9" y="9" width="13" height="13" rx="2" />
                                                <path d="M5 15V5a2 2 0 012-2h10" />
                                            </svg>
                                        </button>
                                    </>
                                ) : (
                                    <span className="text-base text-gray-400 italic">No code yet</span>
                                )}
                            </div>
                            <span className="mt-2 text-xs text-gray-600 dark:text-gray-300 text-center">
          Show this code to the restaurant when you arrive. <b>It’s required to confirm your booking.</b>
        </span>
                        </div>

                        {/* Booking Info Grid */}
                        <div className="mb-8 grid grid-cols-2 gap-x-8 gap-y-3 text-gray-800 dark:text-gray-200">
                            <div>
                                <div className="text-xs text-gray-500 dark:text-gray-400">Date & Time</div>
                                <div className="font-semibold flex items-center gap-2">
                                    <Calendar className="h-4 w-4" />
                                    {selectedBooking.date}
                                </div>
                                <div className="font-semibold flex items-center gap-2">
                                    <Clock className="h-4 w-4" />
                                    {selectedBooking.time}
                                </div>
                            </div>
                            <div>
                                <div className="text-xs text-gray-500 dark:text-gray-400">Restaurant</div>
                                <div className="font-semibold">
                                    {selectedBooking.restaurant?.name || "Restaurant"}
                                </div>
                            </div>
                            <div>
                                <div className="text-xs text-gray-500 dark:text-gray-400">Guests</div>
                                <div className="font-semibold flex items-center gap-2">
                                    <Users className="h-4 w-4" />
                                    {selectedBooking.guests} {selectedBooking.guests === 1 ? "person" : "people"}
                                </div>
                            </div>
                            <div>
                                <div className="text-xs text-gray-500 dark:text-gray-400">Total</div>
                                <div className="font-semibold">{formatCurrency(selectedBooking.total_price)}</div>
                            </div>
                            <div>
                                <div className="text-xs text-gray-500 dark:text-gray-400">Dietary</div>
                                <div className="font-semibold">{selectedBooking.dietary || "—"}</div>
                            </div>
                            <div>
                                <div className="text-xs text-gray-500 dark:text-gray-400">Status</div>
                                <div className="font-semibold">
                                    {selectedBooking.status[0].toUpperCase() +
                                        selectedBooking.status.slice(1)}
                                </div>
                            </div>
                        </div>

                        {/* Actions: Share + Close */}
                        <div className="flex justify-end gap-2">
                            <a
                                href={
                                    selectedBooking.code_to_verify
                                        ? `https://wa.me/?text=${encodeURIComponent(
                                            `🍽️ SurpriseTable Booking\n\nRestaurant: ${selectedBooking.restaurant?.name || "N/A"}\nDate: ${selectedBooking.date}\nTime: ${selectedBooking.time}\nGuests: ${selectedBooking.guests}\nTotal: ${formatCurrency(selectedBooking.total_price)}\nConfirmation Code: ${selectedBooking.code_to_verify || "-"}\nStatus: ${selectedBooking.status[0].toUpperCase() + selectedBooking.status.slice(1)}\n\nShow this code to the restaurant when you arrive!`
                                        )}`
                                        : "#"
                                }
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-2 rounded-md px-4 py-2 font-semibold text-white bg-green-500 hover:bg-green-600 transition focus:outline-none"
                                style={{
                                    pointerEvents: selectedBooking.code_to_verify ? "auto" : "none",
                                    opacity: selectedBooking.code_to_verify ? 1 : 0.6,
                                }}
                            >
                                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 32 32">
                                    <path d="M16 2.967c-7.198 0-13.033 5.835-13.033 13.033 0 2.302.635 4.549 1.838 6.507l-1.927 7.048 7.223-1.892c1.909 1.09 4.062 1.664 6.233 1.664 7.198 0 13.033-5.835 13.033-13.033s-5.835-13.033-13.033-13.033zM16 28.252c-1.953 0-3.878-.512-5.547-1.48l-.397-.235-4.292 1.125 1.146-4.196-.26-.407c-1.165-1.821-1.78-3.938-1.78-6.063 0-6.108 4.968-11.076 11.076-11.076s11.076 4.968 11.076 11.076c0 6.108-4.968 11.076-11.076 11.076z"/>
                                    <path d="M23.587 19.476c-.322-.161-1.909-.942-2.205-1.049-.296-.108-.511-.161-.726.161-.215.322-.833 1.049-1.02 1.263-.187.215-.374.242-.696.081-.322-.161-1.359-.5-2.592-1.596-.958-.857-1.604-1.915-1.792-2.237-.187-.322-.02-.495.141-.656.145-.144.322-.374.483-.561.161-.188.215-.322.322-.537.107-.215.054-.404-.027-.565-.08-.161-.726-1.75-.995-2.396-.263-.631-.53-.546-.726-.556-.188-.008-.403-.009-.619-.009s-.565.081-.862.404c-.296.322-1.129 1.104-1.129 2.693 0 1.59 1.157 3.125 1.318 3.344.161.215 2.282 3.487 5.531 4.553.774.247 1.377.395 1.847.505.775.186 1.48.159 2.037.097.621-.072 1.909-.779 2.179-1.532.269-.752.269-1.397.188-1.532-.08-.134-.295-.215-.617-.376z"/>
                                </svg>
                                Share on WhatsApp
                            </a>
                            <Button onClick={closeDetails} variant="outline">
                                Close
                            </Button>
                        </div>
                    </div>
                </div>
            )}

        </>
    );
};

export default Profile;
